import type { OrgChartDoc } from './types';
import { pbAuthedFetch } from '../../authHeaders';

const CURRENT_URL = '/api/org-charts/current';

export class OrgChartConflictError extends Error {
  doc: OrgChartDoc;
  version: number;
  constructor(serverDoc: OrgChartDoc, serverVersion: number) {
    super('org chart save conflict: the server has a newer version');
    this.name = 'OrgChartConflictError';
    this.doc = serverDoc;
    this.version = serverVersion;
  }
}

export async function fetchCurrentChart(): Promise<{ doc: OrgChartDoc; version: number }> {
  const res = await pbAuthedFetch(CURRENT_URL);
  if (!res.ok) {
    const err = new Error(`org chart fetch failed: ${res.status}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return res.json();
}

const SAVE_MAX_ATTEMPTS = 3;
const SAVE_RETRY_BASE_MS = 300;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface SaveChartBody { doc?: OrgChartDoc; expectedVersion?: number | null; reason?: string }

async function putCurrentChartOnce(body: SaveChartBody): Promise<Response> {
  return pbAuthedFetch(CURRENT_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

async function putWithRetry(body: SaveChartBody, attempt = 0): Promise<{ doc: OrgChartDoc; version: number }> {
  const isLastAttempt = attempt >= SAVE_MAX_ATTEMPTS - 1;
  let res: Response;
  try {
    res = await putCurrentChartOnce(body);
  } catch (networkErr) {
    if (isLastAttempt) throw networkErr;
    await sleep(SAVE_RETRY_BASE_MS * 2 ** attempt);
    return putWithRetry(body, attempt + 1);
  }
  if (res.status === 409) {
    const conflict = await res.json();
    throw new OrgChartConflictError(conflict.doc, conflict.version);
  }
  if (res.ok) return res.json();
  if (res.status >= 500 && !isLastAttempt) {
    await sleep(SAVE_RETRY_BASE_MS * 2 ** attempt);
    return putWithRetry(body, attempt + 1);
  }
  const err = new Error(`org chart save failed: ${res.status}`) as Error & { status: number };
  err.status = res.status;
  throw err;
}

export async function saveCurrentChart(options?: SaveChartBody): Promise<{ doc: OrgChartDoc; version: number }> {
  const body: SaveChartBody = { doc: options?.doc, expectedVersion: options?.expectedVersion };
  if (options?.reason != null) body.reason = options.reason;
  return putWithRetry(body);
}

export async function listChartRevisions(limit?: number): Promise<Array<{ id: number; reason: string; created_by: string | null; created_at: string }>> {
  const qs = limit != null ? `?limit=${encodeURIComponent(limit)}` : '';
  const res = await pbAuthedFetch(`${CURRENT_URL}/versions${qs}`);
  if (!res.ok) {
    const err = new Error(`org chart revisions fetch failed: ${res.status}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function restoreChartRevision(revisionId: string): Promise<{ doc: OrgChartDoc; version: number }> {
  const res = await pbAuthedFetch(`${CURRENT_URL}/versions/${encodeURIComponent(revisionId)}/restore`, { method: 'POST' });
  if (!res.ok) {
    const err = new Error(`org chart restore failed: ${res.status}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export function upgradeDoc(doc: OrgChartDoc): OrgChartDoc {
  if (!doc || typeof doc !== 'object') return doc;
  const version = doc.schemaVersion == null ? 1 : doc.schemaVersion;
  return version === doc.schemaVersion ? doc : { ...doc, schemaVersion: version };
}

export const MAX_JSON_DEPTH = 100;
export const MAX_DOC_BYTES = 6 * 1024 * 1024;

function tooDeep(value: unknown, depth = 0): boolean {
  if (depth > MAX_JSON_DEPTH) return true;
  if (Array.isArray(value)) return value.some((v) => tooDeep(v, depth + 1));
  if (value && typeof value === 'object') return Object.values(value).some((v) => tooDeep(v, depth + 1));
  return false;
}

function tooBig(value: unknown): boolean {
  return new TextEncoder().encode(JSON.stringify(value ?? null)).length > MAX_DOC_BYTES;
}

const REQUIRED_KEYS = ['schemaVersion', 'org', 'sun', 'settings', 'groups'];
const KNOWN_KEYS = new Set(REQUIRED_KEYS);

export function validateChartDoc(doc: unknown): { ok: boolean; errors: string[] } {
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) {
    return { ok: false, errors: ['document must be a JSON object'] };
  }
  const record = doc as Record<string, unknown>;
  const errors: string[] = [];
  for (const key of REQUIRED_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) errors.push(`missing required key "${key}"`);
  }
  for (const key of Object.keys(record)) {
    if (!KNOWN_KEYS.has(key)) errors.push(`unknown top-level key "${key}"`);
  }
  if (Object.prototype.hasOwnProperty.call(record, 'groups')) {
    if (!Array.isArray(record.groups)) {
      errors.push('"groups" must be an array');
    } else {
      (record.groups as unknown[]).forEach((group, gi) => {
        if (!group || typeof group !== 'object' || Array.isArray(group)) { errors.push(`groups[${gi}] must be an object`); return; }
        const gr = group as Record<string, unknown>;
        if (!Array.isArray(gr.programs)) { errors.push(`groups[${gi}].programs must be an array`); return; }
        (gr.programs as unknown[]).forEach((program, pi) => {
          if (!program || typeof program !== 'object' || Array.isArray(program)) { errors.push(`groups[${gi}].programs[${pi}] must be an object`); return; }
          const pr = program as Record<string, unknown>;
          if (!Array.isArray(pr.people)) errors.push(`groups[${gi}].programs[${pi}].people must be an array`);
        });
      });
    }
  }
  if (tooDeep(doc)) errors.push(`document nesting exceeds ${MAX_JSON_DEPTH} levels`);
  if (tooBig(doc)) errors.push(`document exceeds ${MAX_DOC_BYTES} bytes`);
  return { ok: errors.length === 0, errors };
}
